import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CustomButton } from "@/components/common/customButton";
import { CustomSingleSelect } from "@/components/common/inputFields/customSingleSelect";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import {
  getMarketView,
  listMarketViews,
  listReports,
  refreshMarketView,
} from "@/services/marketReportApi/marketReportApi";
import type {
  MarketViewDetail,
  MarketViewListItem,
} from "@/types/marketReport";
import { formatDateTime } from "@/utils/formatDate";
import { MarketViewStatusBadge } from "./components/MarketViewStatusBadge";
import {
  GenerateMarketSummaryModal,
  type GenerateMarketSummaryForm,
} from "./components/GenerateMarketSummaryModal";

type ViewOption = {
  id: string;
  label: string;
  geography: string;
  sector: string;
  status: MarketViewListItem["status"];
};

function buildViewKey(geography: string, sector: string): string {
  return `${geography}\u0000${sector}`;
}

function parseViewKey(key: string): { geography: string; sector: string } | null {
  const parts = key.split("\u0000");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return { geography: parts[0], sector: parts[1] };
}

export function MarketTab() {
  const [views, setViews] = useState<MarketViewListItem[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [detail, setDetail] = useState<MarketViewDetail | null>(null);
  const [reportTitles, setReportTitles] = useState<Record<string, string>>({});
  const [loadingViews, setLoadingViews] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const { errorDialog, showError, hideError } = useErrorDialog();

  const viewOptions = useMemo<ViewOption[]>(
    () =>
      views.map((view) => ({
        id: buildViewKey(view.geography, view.sector),
        label: `${view.geography} · ${view.sector}`,
        geography: view.geography,
        sector: view.sector,
        status: view.status,
      })),
    [views],
  );

  const selectedView = useMemo(
    () => viewOptions.find((option) => option.id === selectedKey) ?? null,
    [viewOptions, selectedKey],
  );

  const loadViews = useCallback(async () => {
    setLoadingViews(true);
    const [viewsRes, reportsRes] = await Promise.all([
      listMarketViews(0, 50),
      listReports(0, 200),
    ]);
    setLoadingViews(false);

    if (viewsRes.success && viewsRes.data) {
      setViews(viewsRes.data);
      if (viewsRes.data.length > 0) {
        setSelectedKey((prev) => {
          if (prev && viewsRes.data!.some(
            (view) => buildViewKey(view.geography, view.sector) === prev,
          )) {
            return prev;
          }
          const first = viewsRes.data![0];
          return buildViewKey(first.geography, first.sector);
        });
      }
    } else {
      showError(
        undefined,
        typeof viewsRes.message === "string"
          ? viewsRes.message
          : "Failed to load market views",
      );
    }

    if (reportsRes.success && reportsRes.data) {
      const titles: Record<string, string> = {};
      for (const report of reportsRes.data) {
        titles[report.id] =
          report.title || report.original_filename || report.id;
      }
      setReportTitles(titles);
    }
  }, [showError]);

  // Id of the latest detail request; a slower response for a previously
  // selected market must not overwrite the summary for the current selection.
  const detailRequestIdRef = useRef(0);

  const loadDetail = useCallback(
    async (geography: string, sector: string) => {
      const requestId = ++detailRequestIdRef.current;
      setLoadingDetail(true);
      setNotFound(false);
      setDetail(null);

      const response = await getMarketView(geography, sector);
      if (detailRequestIdRef.current !== requestId) return;
      setLoadingDetail(false);

      if (response.success && response.data) {
        setDetail(response.data);
        return;
      }

      if (response.status === 404) {
        setNotFound(true);
        return;
      }

      showError(
        undefined,
        typeof response.message === "string"
          ? response.message
          : "Failed to load market summary",
      );
    },
    [showError],
  );

  useEffect(() => {
    void loadViews();
  }, [loadViews]);

  useEffect(() => {
    const parsed = parseViewKey(selectedKey);
    if (!parsed) return;
    void loadDetail(parsed.geography, parsed.sector);
  }, [selectedKey, loadDetail]);

  const generateSummary = async (
    geography: string,
    sector: string,
    topic?: string,
  ) => {
    setRefreshing(true);
    const response = await refreshMarketView({
      geography,
      sector,
      topics: topic ? [topic] : null,
    });
    setRefreshing(false);

    if (response.success && response.data) {
      // Invalidate any in-flight detail load so it cannot replace the freshly
      // generated summary with an older one.
      detailRequestIdRef.current += 1;
      setLoadingDetail(false);
      const key = buildViewKey(geography, sector);
      setSelectedKey(key);
      setDetail(response.data);
      setNotFound(false);
      setGenerateModalOpen(false);
      void loadViews();
      return true;
    }

    showError(
      undefined,
      typeof response.message === "string"
        ? response.message
        : "Failed to generate market summary",
    );
    return false;
  };

  const handleRefresh = async () => {
    const parsed = parseViewKey(selectedKey);
    if (!parsed) return;
    await generateSummary(parsed.geography, parsed.sector);
  };

  const handleGenerateFromModal = async (form: GenerateMarketSummaryForm) => {
    await generateSummary(form.country, form.sector, form.topic || undefined);
  };

  if (loadingViews) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="market-reports-market-tab">
      <Box className="market-reports-toolbar">
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {viewOptions.length > 0 ? (
            <Box sx={{ minWidth: 280, maxWidth: 420, flex: "1 1 280px" }}>
              <CustomSingleSelect
                value={selectedKey}
                onChange={(e) => setSelectedKey(String(e.target.value))}
                options={viewOptions}
                placeholder="Select market"
                labelKey="label"
                fullWidth
              />
            </Box>
          ) : null}
          {selectedView ? (
            <MarketViewStatusBadge status={selectedView.status} />
          ) : null}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <CustomButton
            text="Generate new summary"
            handleButtonPress={() => setGenerateModalOpen(true)}
            isButtonDisabled={refreshing}
            solid={false}
          />
          {selectedView ? (
            <CustomButton
              text={refreshing ? "Refreshing…" : "Refresh summary"}
              handleButtonPress={() => void handleRefresh()}
              isButtonDisabled={refreshing || loadingDetail}
              isLoading={refreshing}
            />
          ) : null}
        </Box>
      </Box>

      {viewOptions.length === 0 ? (
        <Paper elevation={0} className="market-reports-market-empty">
          <Typography className="text-general-page-subheader" sx={{ mb: 1 }}>
            No market summaries yet
          </Typography>
          <Typography className="text-general-page-text" sx={{ mb: 2 }}>
            Summaries appear when reports are ingested for a geography and
            sector. Check coverage labels on uploaded reports to see available
            market pairs, or generate one using the button above.
          </Typography>
          <CustomButton
            text="Generate new summary"
            handleButtonPress={() => setGenerateModalOpen(true)}
            isButtonDisabled={refreshing}
            solid={false}
          />
        </Paper>
      ) : loadingDetail ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : notFound ? (
        <Paper elevation={0} className="market-reports-market-empty">
          <Typography className="text-general-page-subheader" sx={{ mb: 1 }}>
            No summary for {selectedView?.label}
          </Typography>
          <Typography className="text-general-page-text" sx={{ mb: 2 }}>
            Generate a market summary from ingested reports for this geography
            and sector.
          </Typography>
          <CustomButton
            text={refreshing ? "Generating…" : "Generate summary"}
            handleButtonPress={() => void handleRefresh()}
            isButtonDisabled={refreshing}
            isLoading={refreshing}
          />
        </Paper>
      ) : detail ? (
        <Box className="market-reports-market-content">
          {detail.status === "stale" ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              This summary may be out of date. Refresh to incorporate newly
              ingested reports.
            </Alert>
          ) : null}

          <Paper elevation={0} className="market-reports-market-summary">
            <Box className="market-reports-market-summary-header">
              <Typography className="text-general-page-header" sx={{ mb: 0 }}>
                {detail.geography} · {detail.sector}
              </Typography>
              <Typography className="text-general-page-text">
                Updated {formatDateTime(detail.last_refreshed_at)}
              </Typography>
            </Box>

            {detail.summary_text?.trim() ? (
              <div className="market-reports-market-summary-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {detail.summary_text}
                </ReactMarkdown>
              </div>
            ) : (
              <Typography className="text-general-page-text">
                No summary text available yet.
              </Typography>
            )}
          </Paper>

          {detail.source_report_ids.length > 0 ? (
            <Paper elevation={0} className="market-reports-market-sources">
              <Typography className="text-general-page-subheader" sx={{ mb: 1.5 }}>
                Sources ({detail.source_report_ids.length})
              </Typography>
              <Box component="ul" className="market-reports-market-sources-list">
                {detail.source_report_ids.map((reportId) => (
                  <Box component="li" key={reportId}>
                    <Link
                      component={RouterLink}
                      to={`/market-reports/reports/${reportId}`}
                      underline="hover"
                      className="text-general-page-text"
                    >
                      {reportTitles[reportId] ?? reportId}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Paper>
          ) : null}
        </Box>
      ) : null}

      <GenerateMarketSummaryModal
        open={generateModalOpen}
        submitting={refreshing}
        onClose={() => setGenerateModalOpen(false)}
        onSubmit={(form) => void handleGenerateFromModal(form)}
      />

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
}
